<template>
  <b-card v-if="job" class="rent-form">
    <b-card-title class="d-flex align-items-center">
      <h1>${{ job.price | price }}</h1>
      <span>/per day</span>
    </b-card-title>
    <b-form class="form" @submit.prevent="onSubmit">
      <p v-show="!isOwner">Select your hire period</p>
      <div
        :class="[
          'form__section d-flex',
          {
            'form__section--owner': isOwner
          }
        ]"
      >
        <b-form-group>
          <custom-input
            v-model="form.start_date"
            type="text"
            placeholder="Start date"
            readonly
            required
            :disabled="form.$busy"
            :is-invalid="form.$getError('start_date')"
            @click="showDatepicker(true)"
            @focus="showDatepicker(true)"
            @blur="showDatepicker(false)"
          >
            <template slot="icon">
              <b-icon
                icon="caret-down-fill"
                aria-hidden="true"
                @click="showDatepicker(true)"
              ></b-icon>
            </template>
          </custom-input>
          <date-picker
            ref="datepicker"
            v-model="datepickerValue"
            :is-modal="true"
            :is-date-range="true"
            :is-multiple="true"
            :is-auto-closeable="true"
            :calendars-count="2"
            :date-format="datepickerFormat"
            :disabled-dates="datepickerBeforeToday"
            @closed="onSelectedDate"
          ></date-picker>
        </b-form-group>
        <b-form-group>
          <custom-input
            v-model="form.end_date"
            type="text"
            placeholder="End date"
            readonly
            required
            :disabled="form.$busy"
            :is-invalid="form.$getError('end_date')"
            @click="showDatepicker(true)"
            @focus="showDatepicker(true)"
            @blur="showDatepicker(false)"
          >
            <template slot="icon">
              <b-icon
                icon="caret-down-fill"
                aria-hidden="true"
                @click="showDatepicker(true)"
              ></b-icon>
            </template>
          </custom-input>
        </b-form-group>
      </div>

      <transition name="fade">
        <div
          v-if="total && noOfDays"
          class="rent-details d-flex flex-row justify-content-between align-items-center"
        >
          <div>
            <h5>Total</h5>
            <label>${{ job.price | price }} x {{ noOfDays }} day(s)</label>
          </div>
          <h5>$ {{ total | price }}</h5>
        </div>
      </transition>

      <error-message :error="form.$errorMessage"></error-message>
      <error-message :error="form.$getError('start_date')"></error-message>
      <error-message :error="form.$getError('end_date')"></error-message>

      <div class="form__section">
        <b-button
          ref="button"
          type="submit"
          block
          :variant="isOwner ? 'outline-primary' : 'primary'"
          :disabled="isOwner ? !isOwner : !isSubmittable"
          >{{ isOwner ? 'Edit Tool' : 'Rent' }}</b-button
        >
      </div>
    </b-form>
  </b-card>
</template>
<script>
/**
 * ==================================================================================
 * Rent form
 * ==================================================================================
 **/

import { mapState } from 'vuex'
import dayjs from 'dayjs'
import Job from '@/models/Job'
import Form from '@/utils/form'
import Datepicker from '@/utils/mixins/datepicker'
import CustomInput from '@/components/inputs/CustomInput'
import ErrorMessage from '@/components/inputs/ErrorMessage'

export default {
  components: {
    CustomInput,
    ErrorMessage
  },
  mixins: [Datepicker],
  props: {
    job: {
      type: [Job, Object]
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      isAuthenticated: state => state.auth.isAuthenticated
    }),

    total() {
      if (!this.hasDate) return null
      return this.job.price * this.noOfDays
    },

    noOfDays() {
      if (!this.hasDate) return null
      if (this.form.end_date === this.form.start_date) return null
      return dayjs(this.form.end_date).diff(dayjs(this.form.start_date), 'day')
    },

    hasDate() {
      return !!this.form.start_date && !!this.form.end_date
    },

    isSubmittable() {
      return this.noOfDays && this.noOfDays > 0
    },

    isOwner() {
      if (!this.job || !this.user) return false
      return this.job.owner.id === this.user.id
    }
  },
  data: function() {
    return {
      form: new Form({
        start_date: null,
        end_date: null
      })
    }
  },
  methods: {
    async onSubmit() {
      if (!this.isAuthenticated) {
        this.$emit('not-auth', () => {
          this.onSuccess()
        })
      } else {
        this.onSuccess()
      }
    },

    onSuccess() {
      if (this.isOwner) {
        this.$router.push({
          name: 'job.edit-job-page',
          params: {
            id: this.job.permalink,
            title: this.job.name
          }
        })
      } else if (this.isSubmittable && !this.form.busy) {
        this.$router.push({
          name: 'job.rent-job-page',
          query: {
            startDate: this.form.start_date,
            endDate: this.form.end_date
          }
        })
      }
    },

    onSelectedDate(date) {
      let dates = this.datepickerValue.dateRange

      if (dates) {
        this.form.start_date = this.parseDate(dates.start)
        this.form.end_date = this.parseDate(dates.end)

        this.$emit('has-date')
      }
    }
  }
}
</script>
<style scoped lang="scss">
.rent-form /deep/ {
  .card-title {
    margin-top: 12.5px;

    h1 {
      font-size: 2.6rem;
      margin-right: 5px;
    }

    span {
      font-size: 1.1rem;
      font-weight: 600;
      margin-top: 5px;
    }
  }

  .card-body {
    padding: 2rem;
  }

  .form {
    margin-top: 17.5px;

    > p {
      font-size: 1.1rem;
      color: #6c6c6c;
    }

    .custom-input {
      &__icon {
        .b-icon {
          width: 12px;
          color: #6c6c6c;
        }
      }
    }

    .vfc-main-container {
      left: -100%;
      z-index: 10;
    }

    &__section {
      margin-bottom: 0;

      .form-group {
        width: 48.5%;
        margin-right: 3.5%;
        margin-top: 10px;
      }

      .form-group:last-child {
        margin-right: 0%;
      }
    }

    .form__section--owner {
      visibility: hidden;
      height: 1px;
    }
  }

  .rent-details {
    margin: 15px 0 35px 0;

    h5 {
      line-height: 0.75rem;
      margin-bottom: 0;
    }

    label {
      font-size: 0.85rem;
      margin-bottom: 0;
    }
  }
}

@media (max-width: 768px) {
  .rent-form /deep/ {
    .form {
      .vfc-main-container {
        top: 50%;
        left: 0;
      }
    }
  }
}
</style>
